import { useEffect, useState } from 'react'
import { Search, User, X } from 'lucide-react'
import apiService from '../../services/api'

/**
 * F2.4b1 — Recherche / sélection client caisse.
 */
export default function CaisseClientPicker({
  commercantId,
  selectedClient,
  onSelect,
  onClear,
  disabled = false,
}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!q || q.trim().length < 2 || !commercantId) {
      setResults([])
      return undefined
    }
    let cancelled = false
    const t = setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await apiService.searchCaisseClients(q.trim(), commercantId)
        if (!cancelled) setResults(data?.clients || [])
      } catch (e) {
        if (!cancelled) {
          setResults([])
          setError(e?.message || 'Recherche impossible')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [q, commercantId])

  if (selectedClient) {
    const label = [selectedClient.prenom, selectedClient.nom].filter(Boolean).join(' ')
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <div className="flex items-start gap-2">
          <User className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-900">{label || 'Client'}</p>
            {selectedClient.telephone ? (
              <p className="text-xs text-slate-600">{selectedClient.telephone}</p>
            ) : null}
            {selectedClient.email ? (
              <p className="text-xs text-slate-500 truncate">{selectedClient.email}</p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={onClear}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-600 hover:bg-white"
            aria-label="Retirer le client"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
        Client fidélité
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="search"
          value={q}
          disabled={disabled}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nom, téléphone ou e-mail…"
          className="w-full border border-slate-200 rounded-xl pl-9 pr-3 py-3 text-sm focus:border-emerald-500 outline-none min-h-[48px]"
          autoComplete="off"
        />
      </div>
      {loading ? <p className="text-xs text-slate-500">Recherche…</p> : null}
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      {results.length > 0 ? (
        <ul className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100 bg-white">
          {results.map((c) => {
            const label = [c.prenom, c.nom].filter(Boolean).join(' ') || c.email
            return (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onSelect(c)
                    setQ('')
                    setResults([])
                  }}
                  className="w-full text-left px-3 py-3 min-h-[48px] hover:bg-emerald-50 text-sm"
                >
                  <span className="font-medium text-slate-900 block">{label}</span>
                  <span className="text-xs text-slate-500">
                    {[c.telephone, c.email].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
      <p className="text-xs text-slate-500">
        Sans client identifié : vente anonyme, points conservés (pas de récompense).
      </p>
    </div>
  )
}
